import React, { useState, useEffect } from 'react';
import { Control, FieldValues, Path } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, Loader2 } from 'lucide-react';
import { Select, SelectOption } from '../ui/Select';
import { userService } from '@/services/user.service';

interface Course {
  _id: string;
  title: string;
  course_description?: string;
  status?: string;
}

interface CourseMultiSelectProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  label?: string;
  error?: string;
  required?: boolean;
  helperText?: string;
  placeholder?: string;
  disabled?: boolean;
}

export function CourseMultiSelect<T extends FieldValues>({
  name,
  control,
  label = 'Required Courses',
  error,
  required = true,
  helperText = 'Select the courses required for this certification',
  placeholder = 'Search and select courses...',
  disabled = false,
}: CourseMultiSelectProps<T>) {
  // Fetch courses with React Query
  const {
    data: courseData,
    isLoading,
    error: fetchError,
  } = useQuery({
    queryKey: ['course-dropdown'],
    queryFn: () => userService.getCourseDropdown(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  // Convert courses to select options
  const courseOptions: SelectOption[] = courseData?.data?.map((course: Course) => ({
    label: course.title,
    value: course._id,
  })) || [];

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
          <div className="flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-gray-500" />
            <span>{label}</span>
            {required && <span className="text-red-500 ml-1">*</span>}
          </div>
        </label>
      )}

      <div className="relative">
        <Select
          name={name}
          control={control}
          options={courseOptions}
          isMulti
          isSearchable
          placeholder={placeholder}
          isDisabled={disabled || isLoading}
          closeMenuOnSelect={false}
          hideSelectedOptions={true}
        />

        {isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
            <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
          </div>
        )}
      </div>

      {error && (
        <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
          <span>{error}</span>
        </p>
      )}

      {helperText && !error && (
        <p className="text-gray-500 text-xs mt-1">{helperText}</p>
      )}

      {fetchError && (
        <p className="text-orange-600 text-xs mt-1">
          Warning: Failed to load courses. Please refresh the page.
        </p>
      )}
    </div>
  );
}
